import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import common from 'constants/common';
import { scrollMessagesToBottom } from 'components/molecules/smsDashboard/messagesList';
import ChatInput from 'components/atoms/smsDashboard/input';
import useCacheString from 'utils/useCacheString';
import BaseSelect from 'components/atoms/baseSelect';
import BaseButton from 'components/atoms/baseButton';
import CopyImg from 'components/atoms/icons/copyOutlinedIcon';
import { useSendMessage } from 'infrastructure/hooks/smsDashboard/useSendMessage';
import SmsDashboardTemplate from 'components/molecules/smsDashboard/template';
import { CONVERSATION_TYPES } from 'store/smsDashboard';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useSendScheduledMessage } from 'infrastructure/hooks/smsDashboard/useSendScheduledMessage';
import { MessageTypes } from 'constants/messageTypes';
import { useSmsDashobardPatientInfo } from 'infrastructure/hooks/smsDashboard/usePatientInfo';
import { LoginTypes } from 'constants/user';
import { useCloseOpenConversation } from 'infrastructure/hooks/smsDashboard/useCloseOpenConversation';
import BaseDropdownButton from 'components/atoms/baseButton/dropdown';
import SmsTypeIcon from 'components/atoms/icons/sms-type-icon';
import {
  digitalEngagementDataSource,
  digitalEngagementTypes,
} from 'infrastructure/data-sources/digital-engagement';
import { Badge, Tooltip } from 'antd';
import SwitchWithText from 'components/atoms/switch/SwitchWithText';
import classNames from 'classnames';

import s from './styles.module.scss';
import { useAvailabilityAlert } from '../availabilityAlert/use-availability-alert';

import type { DigitalEngagementType } from 'infrastructure/data-sources/digital-engagement';
import type { MessageType } from 'constants/messageTypes';
import type { RootDispatch, RootState } from 'store';
import type { IConversation } from 'infrastructure/interfaces';

const chatInputId = 'smsDashboard.messagesInput';

const selectTheme = (theme: any) => ({
  ...theme,
  borderRadius: 12,
  colors: {
    ...theme.colors,
    ...common.primaryColors,
  },
});

interface IMessageInputProps {
  conversation: IConversation;
  refetch: () => void;
  disabled?: boolean;
  disableTemplate?: boolean;
}

const MessagesInput: React.FC<IMessageInputProps> = (props) => {
  const {
    conversation,
    refetch,
    disabled = false,
    disableTemplate = false,
  } = props;

  const { t } = useTranslation();

  const patient = useSelector((state: RootState) => state.patient);

  const { checkAvailability, Alert } = useAvailabilityAlert({
    patientGuid: conversation.patient.patientGuid,
    withAlert: true,
  });
  const { currentConversation, conversationType, filters } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const { getDetails: patientDetailsLoading } = useSelector(
    (state: RootState) => state.loading.effects.patient,
  );

  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { patientInfo, patientInfoLoading } = useSmsDashobardPatientInfo({
    patientGuid: currentConversation.patient.patientGuid,
  });

  const isUnregisteredConversationType =
    conversationType === CONVERSATION_TYPES.UNREGISTERED_RPM ||
    conversationType === CONVERSATION_TYPES.UNREGISTERED_CCM;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [digitalEngagement, setDigitalEngagement] =
    useState<DigitalEngagementType | null>(null);
  const [messageType, setMessageType] = useState<MessageType>(MessageTypes.sms);

  const [phoneNumber, setPhoneNumber] = useState<{
    label: string;
    value: string | null;
  } | null>({ label: '', value: '' });

  const [inputValue, setInputValue] = useState<string>('');
  const [dgDropdownOpen, setDgDropdownOpen] = useState(false);

  const digitalEngagementTitle =
    digitalEngagement && !dgDropdownOpen
      ? digitalEngagementTypes[digitalEngagement]
      : '';

  const { closeOpenConversation, closeOpenConversationLoading } =
    useCloseOpenConversation({
      type: conversationType,
      read: filters.read,
      closed: filters.closed,
      lastMessageDate: filters.lastMessageDate,
      searchQuery: filters.searchQuery,
      onSuccess() {
        changeCurrentConversation({
          ...currentConversation,
          closed: true,
        });
        notificationController.info({
          message: t('smsDashboard.conversation_is_actioned_successfully', {
            action: 'closed',
          }),
        });
      },
    });

  const phoneOptions = useMemo(() => {
    if (patientDetailsLoading || !patient?.profile) return [];

    const { phone, homeNumber } = patient.profile;

    if (!homeNumber) return [{ label: phone, value: phone }];

    return [
      { label: phone, value: phone },
      { label: homeNumber, value: homeNumber },
    ];
  }, [patient, patientDetailsLoading]);

  const messagePlatformType = useMemo(() => {
    const moduleMap = {
      [CONVERSATION_TYPES.UNREGISTERED_RPM]: CONVERSATION_TYPES.RPM,
      [CONVERSATION_TYPES.UNREGISTERED_CCM]: CONVERSATION_TYPES.CCM,
      [CONVERSATION_TYPES.RPM]: CONVERSATION_TYPES.RPM,
      [CONVERSATION_TYPES.CCM]: CONVERSATION_TYPES.CCM,
    };
    return moduleMap[conversationType] || CONVERSATION_TYPES.RPM;
  }, [conversationType]);

  const { cacheLoading, deleteCache } = useCacheString({
    cacheId: `${chatInputId}.${conversation.id}`,
    value: inputValue,
    setValue: setInputValue,
  });

  const { sendMessage, sendLoading } = useSendMessage({
    onSuccess() {
      setInputValue('');
      scrollMessagesToBottom();
      deleteCache();
      changeCurrentConversation({ ...currentConversation, closed: false });
    },
  });

  const patientPhone = phoneNumber?.value || conversation.patient.patientPhone;

  const { sendScheduledMessage, sendScheduledMessageLoading } =
    useSendScheduledMessage(
      {},
      {
        onSuccess() {
          setInputValue('');
          scrollMessagesToBottom();
          notificationController.info({
            message: t(
              'smsDashboard.message_scheduled_for_closest_available_time',
            ),
          });
        },
      },
    );

  const sendMessageHandler = useCallback(
    async (value: string, type: MessageType, closeConversation = false) => {
      await sendMessage(
        {
          conversationId: conversation.id,
          message: value,
          from: messagePlatformType,
          patientPhone: phoneNumber?.value || conversation.patient.patientPhone,
          messageType: type,
          digitalEngagement: digitalEngagement || undefined,
        },
        {
          onSuccess: async () => {
            if (closeConversation) {
              await closeOpenConversation({
                conversationIds: [conversation.id],
                close: true,
              });
            }
          },
        },
      );
    },
    [
      conversation.id,
      conversation.patient.patientPhone,
      phoneNumber?.value,
      sendMessage,
      digitalEngagement,
    ],
  );

  const sendScheduledMessageHandler = useCallback(
    (value: string, scheduledTime: string, closeConversation = false) => {
      sendScheduledMessage(
        {
          conversationId: conversation.id,
          message: value,
          from: messagePlatformType,
          patientPhone: phoneNumber?.value || conversation.patient.patientPhone,
          scheduledTime,
          patientGuid: patient.guid,
          digitalEngagement: digitalEngagement || undefined,
        },
        {
          onSuccess: async () => {
            if (closeConversation) {
              await closeOpenConversation({
                conversationIds: [conversation.id],
                close: true,
              });
            }
          },
        },
      );
    },
    [
      conversation.id,
      patient.guid,
      conversation.patient.patientPhone,
      phoneNumber?.value,
      sendScheduledMessage,
      messagePlatformType,
      digitalEngagement,
    ],
  );

  const onSubmit = useCallback(
    async (value: string, closeConversation = false) => {
      if (!value) return;
      if (!isUnregisteredConversationType) {
        await refetch();
      }

      if (patient.guid) {
        const { sendLater, sendNow, nextAvailableTime } =
          await checkAvailability(patientPhone);
        if (sendNow) {
          sendMessageHandler(value, messageType, closeConversation);
        } else if (sendLater && nextAvailableTime) {
          sendScheduledMessageHandler(
            value,
            nextAvailableTime,
            closeConversation,
          );
        }
      } else {
        sendMessageHandler(value, messageType, closeConversation);
      }
    },
    [
      refetch,
      patient.guid,
      patientPhone,
      sendMessageHandler,
      sendScheduledMessageHandler,
      isUnregisteredConversationType,
      messageType,
    ],
  );

  const modalSubmit = async (data: string) => {
    setIsModalOpen(false);
    setInputValue(data);
    await onSubmit(data);
  };

  const inputLoading =
    sendLoading ||
    cacheLoading ||
    sendScheduledMessageLoading ||
    closeOpenConversationLoading;

  useEffect(() => {
    setMessageType(MessageTypes.sms);
    setInputValue('');
    setTimeout(() => {
      document.getElementById(chatInputId)?.focus();
    }, 50);
  }, [currentConversation.id]);

  return (
    <div className={s['messages-input-wrapper']}>
      {false &&
        !patientDetailsLoading &&
        phoneOptions.length &&
        !isUnregisteredConversationType && (
          <div className={s['messages-input-phone']}>
            <p>{t('smsDashboard.phone_number_for_reply_to')}:</p>
            <BaseSelect
              options={phoneOptions}
              theme={(theme) => selectTheme(theme)}
              defaultValue={phoneOptions[0] || null}
              classNamePrefix="select-type"
              onChange={(value) =>
                setPhoneNumber(
                  value as unknown as {
                    label: string;
                    value: string | null;
                  } | null,
                )
              }
            />
          </div>
        )}
      <div className={s['chat-form-wrapper']}>
        <BaseButton
          icon={<CopyImg />}
          type="secondary"
          className={s['message-template-button']}
          onClick={() => setIsModalOpen(true)}
          disabled={disableTemplate}
        />
        <Tooltip title={digitalEngagementTitle} placement="top" trigger="hover">
          <Badge
            size="small"
            offset={[-2, 2]}
            count={
              <div
                className={classNames(s.badge, {
                  [s.green]: digitalEngagement,
                })}
              />
            }
          >
            <BaseDropdownButton
              items={[]}
              type="secondary"
              title={digitalEngagementTitle}
              icon={<SmsTypeIcon />}
              className={s['digital-engagement-btn']}
              dropdownProps={{
                menu: {
                  items: digitalEngagementDataSource(true).map((item) => ({
                    key: item.name,
                    label: item.name,
                    onClick: () => setDigitalEngagement(item.id),
                  })),
                },
                open: dgDropdownOpen,
                onOpenChange: (open) => setDgDropdownOpen(open),
                placement: 'topLeft',
                rootClassName: s['digital-engagement-menu'],
                trigger: ['click'],
              }}
            />
          </Badge>
        </Tooltip>
        <SwitchWithText
          checked={messageType === 'chat'}
          onChange={() =>
            setMessageType(
              messageType === MessageTypes.chat
                ? MessageTypes.sms
                : MessageTypes.chat,
            )
          }
          onText="Chat"
          offText="SMS"
          onBackgroundColor={common.activeIconColor}
          offBackgroundColor={common.activeIconColor}
          loading={patientInfoLoading}
          disabled={patientInfo?.profile.loggedInDevice !== LoginTypes.Mobile}
        />

        <ChatInput
          onSubmit={() => onSubmit(inputValue)}
          onSubmitReply={() => onSubmit(inputValue, true)}
          value={inputValue}
          setValue={setInputValue}
          loading={inputLoading}
          id={chatInputId}
          disabled={disabled}
          isClosed={conversation.closed}
        />
      </div>
      <SmsDashboardTemplate
        setIsOpen={setIsModalOpen}
        isOpen={isModalOpen}
        onSubmit={(data) => modalSubmit(data)}
        modalLabel={t('smsDashboard.sms_template')}
      />
      {Alert}
    </div>
  );
};

export default MessagesInput;
